<template>
  <div class="gs-funnel-step">
    <section class="gs-wizard-card gs-funnel-step__summary">
      <h2>Post-purchase destinations</h2>
      <p>{{ $t('frontend_product_step7_configure') }}</p>
      <div class="gs-funnel-step__list-head">
        <strong>Price points</strong>
        <span>Set the post-purchase destination for each price point. Tracking now has its own wizard step.</span>
      </div>
      <div class="gs-funnel-step__accordions">
        <price-point-groups :price-points="pricePoints" v-slot="{ pricePoint, index }">
        <article
          :key="pricePoint.id || index"
          class="gs-funnel-step__accordion"
          :class="{ 'is-open': expandedFunnelIndex === index }"
        >
          <button type="button" class="gs-funnel-step__accordion-head" :aria-expanded="expandedFunnelIndex === index" @click="toggleFunnelAccordion(index)">
            <span class="gs-funnel-step__name">{{ pricePoint.name }}</span>
            <span class="gs-funnel-step__pill">{{ funnelTypeLabel(pricePoint) }}</span>
            <span class="gs-funnel-step__chevron">
              <gs-icon :name="expandedFunnelIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
            </span>
          </button>
          <div v-if="expandedFunnelIndex === index" class="gs-funnel-step__accordion-body">
            <div class="gs-funnel-step__details">
              <div>
                <small>Post-purchase destination</small>
                <strong>{{ funnelDestinationLabel(pricePoint) }}</strong>
              </div>
            </div>
            <gs-button type="button" variant="primary" size="sm" class="gs-funnel-step__edit-action" @click.stop="openFunnelEditor(index)">
              Edit funnel rule
            </gs-button>
          </div>
        </article>
        </price-point-groups>
      </div>
    </section>

    <gs-drawer
      v-model="showFunnelEditor"
      size="xl"
      title="Edit funnel rule"
      :subtitle="editingFunnelPricePoint ? editingFunnelPricePoint.name : ''"
      @close="closeFunnelEditor"
    >
      <div class="gs-funnel-step__editor">
      <div>
        <div v-if="editingFunnelPricePoint" class="gs-funnel-step__drawer-rule">

          <div class="gs-funnel-step__note">
            <info-icon class="h-4 w-4 fill-current inline mr-1 -mt-1"/>
            {{ $t('frontend_product_step7_note') }}
          </div>

          <div class="gs-funnel-step__drawer-grid">
            <div class="gs-funnel-step__field">
              <label>{{ $t('frontend_product_step7_type') }}</label>
              <gd-dropdown
                :key="`funnel-type-${editingFunnelPricePoint.id || editingFunnelIndex}-${normalizedFunnelType(editingFunnelPricePoint)}`"
                :value="normalizedFunnelType(editingFunnelPricePoint)"
                size="full"
                :options="typeOptions"
                @change="setFunnelType(editingFunnelPricePoint, $event)"
              />
            </div>
            <div v-if="normalizedFunnelType(editingFunnelPricePoint) == 1" class="gs-funnel-step__field">
              <label>{{ $t('frontend_product_step7_thankyou_page') }}</label>
              <div class="gs-funnel-step__readonly">{{ editingFunnelPricePoint.post_checkout_details.thankyou_page }}</div>
            </div>
            <div v-if="normalizedFunnelType(editingFunnelPricePoint) == 2" class="gs-funnel-step__field">
              <label>{{ $t('frontend_product_step7_upsell_url') }}</label>
              <text-input
                v-model="editingFunnelPricePoint.post_transaction_details.post_transaction_url"
                :validator="$v.localModel.pricing_points.$each[editingFunnelIndex].post_transaction_details.post_transaction_url"
                :attribute="$t('frontend_product_step7_post_transaction_url')"
                value="editingFunnelPricePoint.post_transaction_details.post_transaction_url"
                :placeholder="$t('frontend_product_step7_post_transaction_url')"
              />
            </div>
          </div>

        </div>
      </div>
      </div>
    </gs-drawer>
  </div>
</template>

<script>
import { required, minLength, requiredIf, url } from 'vuelidate/lib/validators';
import InfoIcon from '@/assets/images/icons/InfoIcon.svg';
import PricePointGroups from '../components/PricePointGroups.vue';

export default {
  name: 'Step7',
  components: {
    InfoIcon,
    PricePointGroups,
  },
  props: {
    model: {
      required: true,
    },
    facebookCoversionList: {
      required: true
    }
  },
  data() {
    return {
      localModel: this.model,
      showFunnelEditor: false,
      postSaleTabs: [
        { title: this.$t('frontend_product_step7_post_transactions'), value: 'postTransactionURL' },
        { title: this.$t('frontend_product_step8_content_access'), value: 'contentAccess' },
      ],
      postSaleTab: 'postTransactionURL',
      pricePointsTab: '',
      expandedFunnelIndex: 0,
      editingFunnelIndex: null,
    };
  },
  validations: {
    localModel: {
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          post_transaction_details: {
            post_transaction_url: {
              required: requiredIf((object) => {
                return object.type == 2;
              }),
              url,
            },
          },
          post_sale_details: {
            $each: {
              link: {
                url,
                required,
              },
            },
          },
        },
      },
    },
  },
  computed: {
    pricePoints() {
      return this.localModel.pricing_points || [];
    },
    editingFunnelPricePoint() {
      return this.editingFunnelIndex === null ? null : this.pricePoints[this.editingFunnelIndex];
    },
    pricePointsTabs() {
      const pricePointTabs = [];

      for (let i = 0; i < this.pricePoints.length; i++) {
        pricePointTabs.push({
          title: this.pricePoints[i].name,
          value: `price_point_${this.pricePoints[i].id}`,
        });
      }

      return pricePointTabs;
    },
    typeOptions() {
      return [
        { value: 1, text: this.$t('frontend_product_step7_thankyou_page') },
        { value: 2, text: this.$t('frontend_product_step7_upsell_page') },
      ];
    },
  },
  watch: {
    localModel: {
      deep: true,
      handler() {
        this.$emit('updateModel', this.localModel);
      },
    },
  },

  mounted() {
    if (
      this.pricePointsTab == '' &&
      this.pricePoints.length > 0
    ) {
      this.pricePointsTab =
        `price_point_${this.pricePoints[0].id}`;
    }
  },
  methods: {
    getHex() {
      let rgb = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(`--vs-${this.color}`);
      rgb = rgb.split(',');
      return (
        `#${(
          (1 << 24) +
          (Number(rgb[0]) << 16) +
          (Number(rgb[1]) << 8) +
          Number(rgb[2])
        )
          .toString(16)
          .slice(1)}`
      );
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    handlePostSaleTabChange(newTab) {
      this.postSaleTab = newTab;
    },

    handlePricePointsTabChange(newTab) {
      this.pricePointsTab = newTab;
    },

    toggleFunnelAccordion(index) {
      this.expandedFunnelIndex = this.expandedFunnelIndex === index ? null : index;
    },

    openFunnelEditor(index) {
      this.editingFunnelIndex = index;
      this.showFunnelEditor = true;
    },

    closeFunnelEditor() {
      this.showFunnelEditor = false;
      this.editingFunnelIndex = null;
    },

    normalizedFunnelType(pricePoint) {
      if (!pricePoint.post_transaction_details) {
        this.$set(pricePoint, 'post_transaction_details', {
          type: 1,
          post_transaction_url: '',
        });
      }

      const rawType = pricePoint.post_transaction_details.type;
      const parsedType = typeof rawType === 'object' && rawType !== null
        ? Number(rawType.value)
        : Number(rawType);

      if (parsedType === 1 || parsedType === 2) {
        if (pricePoint.post_transaction_details.type !== parsedType) {
          this.$set(pricePoint.post_transaction_details, 'type', parsedType);
        }

        return parsedType;
      }

      const inferredType = pricePoint.post_transaction_details.post_transaction_url ? 2 : 1;
      this.$set(pricePoint.post_transaction_details, 'type', inferredType);
      return inferredType;
    },

    setFunnelType(pricePoint, value) {
      const nextType = typeof value === 'object' && value !== null ? value.value : value;
      this.$set(pricePoint.post_transaction_details, 'type', Number(nextType));
    },

    funnelTypeLabel(pricePoint) {
      return this.normalizedFunnelType(pricePoint) == 2
        ? this.$t('frontend_product_step7_upsell_page')
        : this.$t('frontend_product_step7_thankyou_page');
    },

    funnelDestinationLabel(pricePoint) {
      if (this.normalizedFunnelType(pricePoint) == 2) {
        return pricePoint.post_transaction_details.post_transaction_url || 'No upsell URL set';
      }

      return pricePoint.post_checkout_details.thankyou_page || 'Default thank you page';
    },
  },
};
</script>
<style>
.gs-funnel-step {
  display: flex;
  flex-direction: column;
  gap: 14px;
}


.gs-funnel-step__summary h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}


.gs-funnel-step__list-head {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: baseline;
  column-gap: 16px;
  margin: 16px 0 8px;
  padding-top: 14px;
  border-top: 1px solid var(--gs-border);
}

.gs-funnel-step__list-head strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-funnel-step__list-head span {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.35;
}


.gs-funnel-step__accordions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gs-funnel-step__accordion {
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  overflow: hidden;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.gs-funnel-step__accordion.is-open {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-funnel-step__accordion-head {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) auto 24px;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  text-align: left;
  background: #ffffff;
  border: 0;
  cursor: pointer;
}

.gs-funnel-step__name {
  min-width: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-funnel-step__pill {
  padding: 7px 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-funnel-step__chevron {
  color: var(--gs-muted-foreground);
  font-size: 18px;
  text-align: right;
}

.gs-funnel-step__accordion-body {
  padding: 0 16px 16px;
  border-top: 1px solid var(--gs-border);
}

.gs-funnel-step__details {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  margin-top: 14px;
}

.gs-funnel-step__details div {
  min-width: 0;
  padding: 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-funnel-step__details small {
  display: block;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.gs-funnel-step__details strong {
  display: block;
  min-width: 0;
  margin-top: 5px;
  color: var(--gs-foreground);
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-funnel-step__edit-action {
  margin-top: 14px;
}

.gs-funnel-step__note {
  margin-bottom: 18px;
  padding: 10px 12px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight:500;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}


.gs-funnel-step__drawer-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  margin-top: 18px;
}

.gs-funnel-step__field {
  min-width: 0;
  margin-bottom: 18px;
}

.gs-funnel-step__field label {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
}

.gs-funnel-step__readonly {
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  font-size: 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

</style>
